import React, { useState, useMemo } from 'react';
import { Helmet } from 'react-helmet';
import Header from '../../components/ui/Header';
import Breadcrumb from '../../components/ui/Breadcrumb';
import Button from '../../components/ui/Button';
import Select from '../../components/ui/Select';
import Input from '../../components/ui/Input';
import Icon from '../../components/AppIcon';

const Careers = () => {
  const [filters, setFilters] = useState({
    search: '',
    location: '',
    department: ''
  });

  // Mock job data - using active jobs from job postings
  const availableJobs = [
    {
      id: 1,
      title: "Senior Software Engineer",
      department: "Engineering",
      location: "San Francisco, CA",
      type: "Full-time",
      description: "Join our engineering team to build scalable web applications using modern technologies. You\'ll collaborate with cross-functional teams to deliver high-quality software solutions.",
      requirements: [
        "5+ years of experience in software development",
        "Proficiency in JavaScript, React, and Node.js",
        "Experience with cloud platforms (AWS, GCP, or Azure)",
        "Strong understanding of software engineering principles"
      ],
      salary: "$120,000 - $160,000",
      experience: "Senior Level",
      postedDate: "2024-10-15"
    },
    {
      id: 2,
      title: "Product Manager",
      department: "Product",
      location: "Remote",
      type: "Full-time",
      description: "Drive the strategy and execution of our core product features. Work closely with engineering, design, and business stakeholders to deliver exceptional user experiences.",
      requirements: [
        "3+ years of product management experience",
        "Strong analytical and data-driven decision making skills",
        "Experience with agile development methodologies",
        "Excellent communication and leadership abilities"
      ],
      salary: "$100,000 - $140,000",
      experience: "Mid Level",
      postedDate: "2024-10-14"
    },
    {
      id: 3,
      title: "Marketing Specialist",
      department: "Marketing",
      location: "Austin, TX",
      type: "Full-time",
      description: "Develop and execute comprehensive marketing campaigns that drive brand awareness and customer acquisition across multiple channels.",
      requirements: [
        "2+ years of marketing experience",
        "Experience with digital marketing tools and platforms",
        "Strong writing and content creation skills",
        "Knowledge of SEO, SEM, and social media marketing"
      ],
      salary: "$60,000 - $80,000",
      experience: "Entry Level",
      postedDate: "2024-10-12"
    },
    {
      id: 4,
      title: "Data Scientist",
      department: "Engineering",
      location: "Seattle, WA",
      type: "Full-time",
      description: "Join our analytics team to drive data-informed decision making across the organization. Work with large datasets to uncover insights and build predictive models.",
      requirements: [
        "Master's degree in Data Science, Statistics, or related field",
        "3+ years of experience in data analysis and machine learning",
        "Proficiency in Python, R, and SQL",
        "Experience with data visualization tools"
      ],
      salary: "$110,000 - $150,000",
      experience: "Mid Level",
      postedDate: "2024-10-11"
    },
    {
      id: 5,
      title: "DevOps Engineer",
      department: "Engineering",
      location: "Remote",
      type: "Full-time",
      description: "Build and maintain our cloud infrastructure. Work on automation, monitoring, and deployment pipelines to ensure reliable and scalable systems.",
      requirements: [
        "4+ years of DevOps or infrastructure experience",
        "Experience with AWS, Docker, and Kubernetes",
        "Proficiency in scripting languages (Python, Bash)",
        "Knowledge of CI/CD pipelines and automation tools"
      ],
      salary: "$100,000 - $130,000",
      experience: "Mid Level",
      postedDate: "2024-10-09"
    },
    {
      id: 6,
      title: "Content Writer",
      department: "Marketing",
      location: "Remote",
      type: "Part-time",
      description: "Create engaging blog posts, articles, and marketing materials that educate and inspire our audience. Work closely with the marketing team to develop content strategies.",
      requirements: [
        "2+ years of content writing experience",
        "Excellent writing and editing skills",
        "Experience with SEO best practices",
        "Ability to write for different audiences and formats"
      ],
      salary: "$30 - $50 per hour",
      experience: "Entry Level",
      postedDate: "2024-10-08"
    }
  ];

  // Filter options
  const locationOptions = [
    { value: '', label: 'All Locations' },
    { value: 'Remote', label: 'Remote' },
    { value: 'San Francisco, CA', label: 'San Francisco, CA' },
    { value: 'Austin, TX', label: 'Austin, TX' },
    { value: 'Seattle, WA', label: 'Seattle, WA' },
    { value: 'New York, NY', label: 'New York, NY' },
    { value: 'Chicago, IL', label: 'Chicago, IL' }
  ];

  const departmentOptions = [
    { value: '', label: 'All Departments' },
    { value: 'Engineering', label: 'Engineering' },
    { value: 'Product', label: 'Product' },
    { value: 'Marketing', label: 'Marketing' },
    { value: 'Design', label: 'Design' },
    { value: 'Sales', label: 'Sales' },
    { value: 'HR', label: 'Human Resources' }
  ];

  // Filtered jobs
  const filteredJobs = useMemo(() => {
    return availableJobs?.filter(job => {
      const matchesSearch = job?.title?.toLowerCase()?.includes(filters?.search?.toLowerCase()) ||
                           job?.department?.toLowerCase()?.includes(filters?.search?.toLowerCase());
      const matchesLocation = !filters?.location || job?.location === filters?.location;
      const matchesDepartment = !filters?.department || job?.department === filters?.department;
      
      return matchesSearch && matchesLocation && matchesDepartment;
    });
  }, [availableJobs, filters]);

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({
      ...prev,
      [key]: value
    }));
  };

  const handleApply = (job) => {
    // Mock apply functionality - could redirect to application form
    window.open(`mailto:careers@company.com?subject=Application for ${job?.title}&body=I am interested in applying for the ${job?.title} position.`, '_blank');
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date?.toLocaleDateString('en-US', { 
      year: 'numeric', 
      month: 'long', 
      day: 'numeric' 
    });
  };

  return (
    <div className="min-h-screen bg-background">
      <Helmet>
        <title>Careers - Join Our Team | ATS Company</title>
        <meta name="description" content="Discover exciting career opportunities at ATS Company. We're hiring talented professionals across Engineering, Product, Marketing, and more. Apply today!" />
        <meta name="keywords" content="careers, jobs, employment, engineering jobs, product manager, marketing specialist, remote work, software engineer" />
        <meta property="og:title" content="Careers - Join Our Team | ATS Company" />
        <meta property="og:description" content="Discover exciting career opportunities at ATS Company. We're hiring talented professionals across multiple departments." />
        <meta property="og:type" content="website" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Careers - Join Our Team | ATS Company" />
        <meta name="twitter:description" content="Discover exciting career opportunities at ATS Company. We're hiring talented professionals across multiple departments." />
        <link rel="canonical" href={window?.location?.href} />
      </Helmet>

      <Header />
      <main className="pt-16">
        <div className="max-w-7xl mx-auto px-6 py-8">
          <Breadcrumb />
          
          {/* Hero Section */}
          <div className="text-center mb-12">
            <h1 className="text-4xl font-bold text-foreground mb-4">
              Join Our Team
            </h1>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              We're building the future of talent acquisition. Join our passionate team and help transform how companies find and hire great talent.
            </p>
          </div>

          {/* Stats Section */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-12">
            <div className="bg-card border border-border rounded-lg p-6 text-center">
              <div className="w-12 h-12 bg-primary/10 rounded-lg flex items-center justify-center mx-auto mb-3">
                <Icon name="Users" size={24} className="text-primary" />
              </div>
              <h3 className="text-2xl font-bold text-foreground">{availableJobs?.length}</h3>
              <p className="text-muted-foreground">Open Positions</p>
            </div>
            
            <div className="bg-card border border-border rounded-lg p-6 text-center">
              <div className="w-12 h-12 bg-success/10 rounded-lg flex items-center justify-center mx-auto mb-3">
                <Icon name="MapPin" size={24} className="text-success" />
              </div>
              <h3 className="text-2xl font-bold text-foreground">5+</h3>
              <p className="text-muted-foreground">Global Locations</p>
            </div>
            
            <div className="bg-card border border-border rounded-lg p-6 text-center">
              <div className="w-12 h-12 bg-accent/10 rounded-lg flex items-center justify-center mx-auto mb-3">
                <Icon name="Briefcase" size={24} className="text-accent" />
              </div>
              <h3 className="text-2xl font-bold text-foreground">{new Set(availableJobs?.map(job => job?.department))?.size}</h3>
              <p className="text-muted-foreground">Departments</p>
            </div>
          </div>

          {/* Filters */}
          <div className="bg-card border border-border rounded-lg p-6 mb-8">
            <h2 className="text-lg font-semibold text-foreground mb-4">Find Your Perfect Role</h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <Input
                type="search"
                placeholder="Search job titles..."
                value={filters?.search}
                onChange={(e) => handleFilterChange('search', e?.target?.value)}
              />

              <Select
                options={locationOptions}
                value={filters?.location}
                onChange={(value) => handleFilterChange('location', value)}
                placeholder="Filter by location"
              />

              <Select
                options={departmentOptions}
                value={filters?.department}
                onChange={(value) => handleFilterChange('department', value)}
                placeholder="Filter by department"
              />
            </div>
          </div>

          {/* Job Listings */}
          <div className="space-y-6">
            <div className="flex items-center justify-between">
              <h2 className="text-2xl font-bold text-foreground">
                {filteredJobs?.length} Open Position{filteredJobs?.length !== 1 ? 's' : ''}
              </h2>
            </div>

            {filteredJobs?.length > 0 ? (
              <div className="grid gap-6">
                {filteredJobs?.map((job) => (
                  <div key={job?.id} className="bg-card border border-border rounded-lg p-6 hover:shadow-md transition-shadow">
                    <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between">
                      <div className="flex-1">
                        <div className="flex flex-wrap items-center gap-2 mb-3">
                          <h3 className="text-xl font-semibold text-foreground">{job?.title}</h3>
                          <span className="px-2 py-1 bg-primary/10 text-primary text-sm rounded-md">
                            {job?.type}
                          </span>
                        </div>
                        
                        <div className="flex flex-wrap items-center gap-4 text-sm text-muted-foreground mb-3">
                          <div className="flex items-center gap-1">
                            <Icon name="Building" size={16} />
                            <span>{job?.department}</span>
                          </div>
                          <div className="flex items-center gap-1">
                            <Icon name="MapPin" size={16} />
                            <span>{job?.location}</span>
                          </div>
                          <div className="flex items-center gap-1">
                            <Icon name="DollarSign" size={16} />
                            <span>{job?.salary}</span>
                          </div>
                          <div className="flex items-center gap-1">
                            <Icon name="Calendar" size={16} />
                            <span>Posted {formatDate(job?.postedDate)}</span>
                          </div>
                        </div>
                        
                        <p className="text-muted-foreground mb-4">{job?.description}</p>
                        
                        <div className="mb-4">
                          <h4 className="font-medium text-foreground mb-2">Key Requirements:</h4>
                          <ul className="text-sm text-muted-foreground space-y-1">
                            {job?.requirements?.slice(0, 3)?.map((req, index) => (
                              <li key={index} className="flex items-start gap-2">
                                <Icon name="Check" size={16} className="text-success mt-0.5 flex-shrink-0" />
                                <span>{req}</span>
                              </li>
                            ))}
                          </ul>
                        </div>
                      </div>
                      
                      <div className="flex flex-col items-start lg:items-end gap-3 lg:ml-6">
                        <div className="text-right">
                          <p className="text-sm text-muted-foreground">Experience Level</p>
                          <p className="font-medium text-foreground">{job?.experience}</p>
                        </div>
                        <Button
                          variant="default"
                          onClick={() => handleApply(job)}
                          iconName="Send"
                          iconPosition="right"
                        >
                          Apply Now
                        </Button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-12">
                <div className="w-16 h-16 bg-muted rounded-full flex items-center justify-center mx-auto mb-4">
                  <Icon name="Search" size={32} className="text-muted-foreground" />
                </div>
                <h3 className="text-lg font-medium text-foreground mb-2">No positions found</h3>
                <p className="text-muted-foreground mb-6">
                  Try adjusting your search criteria or check back later for new opportunities.
                </p>
                <Button
                  variant="outline"
                  onClick={() => setFilters({ search: '', location: '', department: '' })}
                >
                  Clear Filters
                </Button>
              </div>
            )}
          </div>

          {/* Footer CTA */}
          <div className="bg-primary/5 border border-primary/20 rounded-lg p-8 mt-12 text-center">
            <h3 className="text-2xl font-bold text-foreground mb-4">
              Don't see the right role?
            </h3>
            <p className="text-muted-foreground mb-6 max-w-2xl mx-auto">
              We're always looking for exceptional talent. Send us your resume and we'll keep you in mind for future opportunities that match your skills and interests.
            </p>
            <Button
              variant="default"
              onClick={() => window.open('mailto:careers@company.com?subject=General Application&body=I am interested in future opportunities with your company.', '_blank')}
              iconName="Mail"
              iconPosition="left"
            >
              Send General Application
            </Button>
          </div>
        </div>
      </main>
    </div>
  );
};

export default Careers;